import type {
  MRT_RowSelectionState,
  MRT_TableInstance,
} from "material-react-table";
import type { CheckboxProps } from "@mui/material";

/**
 * Header-checkbox behavior shared by the candidate tables (discovery,
 * evaluation table + matrix, final selection).
 *
 * The row selection is id-keyed and survives pagination so toolbar actions
 * can operate on it, but that makes MRT's symmetric select-all modes wrong:
 * page mode leaves off-page selections un-clearable, all mode selects
 * hundreds of rows at once. The header checkbox therefore gets asymmetric
 * semantics:
 *
 * - page not fully selected -> ADD the visible page to the selection
 *   (cross-page accumulation: select page 1, paginate, select page 2)
 * - page fully selected (checkbox checked) -> clear the ENTIRE selection,
 *   including rows parked on other pages
 *
 * Everything is read from the `table` instance at click time — never from a
 * closed-over selection value, which goes stale inside MRT's memoized header
 * cells. MRT spreads these props after its internal handler, so the onChange
 * override is authoritative (verified against material-react-table v1).
 */
export const selectVisibleDeselectAllProps =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MRT v1's own TData constraint
  <TData extends Record<string, any>>(
    onRowSelectionChange: (selection: MRT_RowSelectionState) => void,
    extraProps: Partial<CheckboxProps> = {},
  ) =>
  ({ table }: { table: MRT_TableInstance<TData> }): CheckboxProps => ({
    ...extraProps,
    onChange: () => {
      const pageRows = table.getRowModel().rows;
      const pageFullySelected =
        pageRows.length > 0 && pageRows.every((row) => row.getIsSelected());
      if (pageFullySelected) {
        onRowSelectionChange({});
        return;
      }
      onRowSelectionChange({
        ...table.getState().rowSelection,
        ...Object.fromEntries(pageRows.map((row) => [row.id, true])),
      });
    },
  });
